import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { directivesSeo } from '../../seo/directivesSeo';

/**
 * Pose dans <head>, à chaque changement de route, la balise robots « noindex », la
 * canonique et la description de la page (règles : src/seo/directivesSeo.ts).
 * À placer dans <BrowserRouter>, à côté de TelemetryTracker.
 */

// Description générique de public/index.html, rétablie sur les pages sans description propre.
const descriptionParDefaut = (): string | null =>
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.dataset.defaut ?? null;

function balise<T extends HTMLElement>(selecteur: string, creer: () => T): T {
  const existante = document.head.querySelector<T>(selecteur);
  if (existante) return existante;
  const nouvelle = creer();
  document.head.appendChild(nouvelle);
  return nouvelle;
}

export function appliquerSeo(hote: string, chemin: string): void {
  const d = directivesSeo(hote, chemin);

  const robots = document.head.querySelector('meta[name="robots"]');
  if (d.indexable) {
    robots?.remove();
  } else {
    balise('meta[name="robots"]', () => Object.assign(document.createElement('meta'), { name: 'robots' }))
      .setAttribute('content', 'noindex');
  }

  const canonique = document.head.querySelector('link[rel="canonical"]');
  if (d.canonique) {
    balise('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }))
      .setAttribute('href', d.canonique);
  } else {
    canonique?.remove();
  }

  const description = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (description) {
    if (description.dataset.defaut === undefined) description.dataset.defaut = description.content;
    description.content = d.description ?? descriptionParDefaut() ?? description.content;
  }
}

const SeoTags: React.FC<{ hote?: string }> = ({ hote }) => {
  const location = useLocation();

  useEffect(() => {
    appliquerSeo(hote ?? window.location.hostname, location.pathname);
  }, [hote, location.pathname]);

  return null;
};

export default SeoTags;
